import { useEffect, useRef } from "react";

export const DEMO_DURATION = 14;

export const C = {
  ink: "#070A0F",
  land: "#0f161f",
  landEdge: "#2c3a4a",
  grid: "rgba(255,255,255,0.035)",
  fg: "#F0F6FC",
  fg2: "#A3AFBC",
  fg3: "#8593A1",
  cyan: "#00F2FE",
  amber: "#FF9F0A",
  red: "#ff5a52",
  green: "#3dfc8f",
};
const MONO = '"JetBrains Mono", ui-monospace, monospace';

export const clamp01 = (x) => Math.min(Math.max(x, 0), 1);
export const smooth = (a, b, x) => {
  const t = clamp01((x - a) / (b - a));
  return t * t * (3 - 2 * t);
};
export const lerp = (a, b, t) => a + (b - a) * t;

export const text = (ctx, s, x, y, { color = C.fg2, size = 11, align = "left", weight = 500 } = {}) => {
  ctx.font = `${weight} ${size}px ${MONO}`;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = "middle";
  ctx.fillText(s, x, y);
};

export const background = (ctx, w, h, step = 44) => {
  ctx.fillStyle = C.ink;
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = C.grid;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = step; x < w; x += step) { ctx.moveTo(x, 0); ctx.lineTo(x, h); }
  for (let y = step; y < h; y += step) { ctx.moveTo(0, y); ctx.lineTo(w, y); }
  ctx.stroke();
};

export const poly = (ctx, pts, w, h, fill = C.land, stroke = C.landEdge) => {
  ctx.beginPath();
  pts.forEach(([x, y], i) => (i ? ctx.lineTo(x * w, y * h) : ctx.moveTo(x * w, y * h)));
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 1;
  ctx.stroke();
};

export const toPx = (pts, w, h) => pts.map(([x, y]) => [x * w, y * h]);

export const along = (px, u) => {
  const segs = [];
  let total = 0;
  for (let i = 1; i < px.length; i++) {
    const l = Math.hypot(px[i][0] - px[i - 1][0], px[i][1] - px[i - 1][1]);
    segs.push(l);
    total += l;
  }
  let d = clamp01(u) * total;
  for (let i = 0; i < segs.length; i++) {
    if (d <= segs[i] || i === segs.length - 1) {
      const k = segs[i] ? Math.min(d / segs[i], 1) : 0;
      const a = px[i];
      const b = px[i + 1];
      return { x: lerp(a[0], b[0], k), y: lerp(a[1], b[1], k), ang: Math.atan2(b[1] - a[1], b[0] - a[0]) };
    }
    d -= segs[i];
  }
  return { x: px[0][0], y: px[0][1], ang: 0 };
};

export const pathLine = (ctx, px, from, to, { color = C.cyan, width = 1.5, dash = [], alpha = 1 } = {}) => {
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.setLineDash(dash);
  ctx.beginPath();
  const steps = 60;
  for (let i = 0; i <= steps; i++) {
    const p = along(px, lerp(from, to, i / steps));
    if (i) ctx.lineTo(p.x, p.y);
    else ctx.moveTo(p.x, p.y);
  }
  ctx.stroke();
  ctx.restore();
};

export const vessel = (ctx, x, y, ang, len, color, fill = true) => {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(ang);
  const b = len * 0.28;
  ctx.beginPath();
  ctx.moveTo(len * 0.55, 0);
  ctx.lineTo(len * 0.2, -b);
  ctx.lineTo(-len * 0.45, -b);
  ctx.lineTo(-len * 0.45, b);
  ctx.lineTo(len * 0.2, b);
  ctx.closePath();
  if (fill) {
    ctx.fillStyle = color;
    ctx.fill();
  }
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.2;
  ctx.stroke();
  ctx.restore();
};

export const glow = (ctx, x, y, r, color, alpha = 1) => {
  ctx.save();
  ctx.globalAlpha = alpha;
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, color);
  g.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};

export const panel = (ctx, x, y, rows, { width = 196, title } = {}) => {
  const lh = 17;
  const hgt = rows.length * lh + (title ? 30 : 14);
  ctx.save();
  ctx.fillStyle = "rgba(7,10,15,0.78)";
  ctx.strokeStyle = "rgba(255,255,255,0.12)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(x, y, width, hgt, 6);
  ctx.fill();
  ctx.stroke();
  let yy = y + 15;
  if (title) {
    text(ctx, title, x + 12, yy, { color: C.fg3, size: 10 });
    yy += 17;
  }
  rows.forEach(([k, v, color]) => {
    text(ctx, k, x + 12, yy, { color: C.fg3, size: 10.5 });
    text(ctx, v, x + width - 12, yy, { color: color || C.fg, size: 10.5, align: "right" });
    yy += lh;
  });
  ctx.restore();
  return hgt;
};

export const banner = (ctx, w, y, label, color) => {
  ctx.save();
  ctx.font = `500 11px ${MONO}`;
  const tw = ctx.measureText(label).width + 28;
  const x = (w - tw) / 2;
  ctx.fillStyle = "rgba(7,10,15,0.85)";
  ctx.strokeStyle = color;
  ctx.beginPath();
  ctx.roundRect(x, y, tw, 26, 13);
  ctx.fill();
  ctx.stroke();
  text(ctx, label, w / 2, y + 13.5, { color, size: 11, align: "center" });
  ctx.restore();
};

export const useCanvasLoop = (draw, time) => {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !draw) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let w = 0;
    let h = 0;
    let raf = 0;
    let visible = true;
    let lastTime = -1;
    const resize = () => {
      const r = canvas.parentElement.getBoundingClientRect();
      w = r.width;
      h = r.height;
      canvas.width = Math.max(1, Math.round(w * dpr));
      canvas.height = Math.max(1, Math.round(h * dpr));
      lastTime = -1;
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas.parentElement);
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; });
    io.observe(canvas);
    const loop = () => {
      raf = requestAnimationFrame(loop);
      if (!visible || w < 2) return;
      const seconds = time.get();
      if (seconds === lastTime) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      draw(ctx, w, h, seconds);
      lastTime = seconds;
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      io.disconnect();
    };
  }, [draw, time]);
  return ref;
};
